import { useCallback, useEffect, useMemo, useState } from 'react';
import { getHistoricoCuidados } from '../../api/api';

/**
 * Hook para buscar histórico de cuidados com paginação e filtros.
 */
const DEFAULT_PARAMS = {
	page: 1,
	limit: 10,
	sortBy: 'data_hora',
	order: 'desc',
	startDate: null,
	endDate: null,
	tipo: null
};

export function useHistoricoCuidados(pacienteId, initialParams = {}) {
	const [params, setParams] = useState({ ...DEFAULT_PARAMS, ...initialParams });
	const [data, setData] = useState([]);
	const [page, setPage] = useState(1);
	const [total, setTotal] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	const effectiveParams = useMemo(() => ({ ...params, page }), [params, page]);

	const fetchData = useCallback(async () => {
		if (!pacienteId) return;
		setIsLoading(true);
		setError(null);
		try {
			// Nova rota retorna { success, data: [...] }
			const { data: resp } = await getHistoricoCuidados(pacienteId, { limit: Number(effectiveParams.limit || 10) * 5 });
			const full = Array.isArray(resp?.data) ? resp.data : Array.isArray(resp) ? resp : [];
			
			// Função auxiliar para extrair hora_agendada de um registro
			const extrairHoraAgendada = (item) => {
				if (!item) return null;
				
				// Tenta extrair de diferentes locais possíveis
				if (item.hora_agendada) {
					const date = new Date(item.hora_agendada);
					if (!isNaN(date.getTime())) return date.getTime();
				}
				
				// Tenta do conteudo_detalhado (pode ser string formatada ou objeto)
				if (item.conteudo_detalhado) {
					let conteudo;
					try {
						conteudo = typeof item.conteudo_detalhado === 'string' 
							? JSON.parse(item.conteudo_detalhado) 
							: item.conteudo_detalhado;
					} catch (e) {
						// Se falhar o parse, tenta usar como string direta
						conteudo = item.conteudo_detalhado;
					}
					
					if (conteudo?.hora_agendada) {
						const date = new Date(conteudo.hora_agendada);
						if (!isNaN(date.getTime())) return date.getTime();
					}
				}
				
				return null;
			};
			
			// Função auxiliar para obter data de ordenação (prioriza hora_agendada)
			const obterDataOrdenacao = (item) => {
				const horaAgendada = extrairHoraAgendada(item);
				if (horaAgendada !== null) return horaAgendada;
				
				// Fallback para data_hora_registro ou data_hora
				const dataRegistro = item.data_hora_registro 
					? new Date(item.data_hora_registro).getTime()
					: null;
				const dataHora = item.data_hora 
					? new Date(item.data_hora).getTime()
					: null;
				
				return dataRegistro || dataHora || 0;
			};
			
			// Ordena client-side priorizando hora_agendada quando disponível
			const sorted = [...full].sort((a, b) => {
				const dateA = obterDataOrdenacao(a);
				const dateB = obterDataOrdenacao(b);
				return dateB - dateA; // Decrescente (mais recente primeiro)
			});
			setTotal(sorted.length);
			// Paginação client-side
			const limit = Number(effectiveParams.limit || 10);
			const startIdx = (page - 1) * limit;
			const paged = sorted.slice(startIdx, startIdx + limit);
			setData(paged);
		} catch (err) {
			setError(err?.response?.data?.message || err?.message || 'Erro ao carregar histórico de cuidados.');
		} finally {
			setIsLoading(false);
		}
	}, [pacienteId, effectiveParams]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const refetch = useCallback(() => {
		fetchData();
	}, [fetchData]);

	const hasMore = useMemo(() => {
		const limit = Number(effectiveParams.limit || 10);
		return page * limit < total;
	}, [page, total, effectiveParams.limit]);

	return {
		data,
		page,
		total,
		hasMore,
		isLoading,
		error,
		refetch,
		setParams,
		setPage
	};
}

export default useHistoricoCuidados;


